/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import { getByIdAsync, getVariantsByDimensionValuesAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/ProductsDataActions.g';
import { SimpleProduct } from '@msdyn365-commerce/retail-proxy/dist/Entities/CommerceTypes.g';

// tslint:disable-next-line: completed-docs
export class BuyboxGetDimensionVariantAvailabilityInput implements IActionInput {
    public channelId: number;
    public productId: number;
    constructor(productId: number, channelId: number) {
        this.channelId = channelId;
        this.productId = productId;
    }
    public getCacheKey = () => `AllMFRMProductVariantIDs-${this.productId}`;
    public getCacheObjectType = () => 'AllMFRMProductVariantIDs';
    public dataCacheType = (): CacheType => 'application';
}

export interface IBuyboxGetDimensionVariantAvailabilityActionData {
    variantID: number;
    dimensionRecordID: number;
    itemID: string;
    sizeValue: string;
    isAvailable: boolean;
}

const createInput = (inputData: ICreateActionContext<IGeneric<IAny>>): IActionInput => {
    if (inputData && inputData.requestContext && inputData.requestContext.query && inputData.requestContext.query.productId) {
        return new BuyboxGetDimensionVariantAvailabilityInput(
            +inputData.requestContext.query.productId,
            +inputData.requestContext.apiSettings.channelId
        );
    } else if (inputData && inputData.requestContext && inputData.requestContext.urlTokens && inputData.requestContext.urlTokens.itemId) {
        return new BuyboxGetDimensionVariantAvailabilityInput(
            +inputData.requestContext.urlTokens.itemId,
            +inputData.requestContext.apiSettings.channelId
        );
    } else if (inputData && inputData.config && inputData.config.productId) {
        return new BuyboxGetDimensionVariantAvailabilityInput(inputData.config.productId, inputData.requestContext.apiSettings.channelId);
    } else {
        throw new Error('Unable to create SelectedVariantInput, no productId found on module config or query');
    }
};

export async function getAllVariantsForProduct(
    input: BuyboxGetDimensionVariantAvailabilityInput,
    ctx: IActionContext
): Promise<SimpleProduct[]> {
    const productResponse = await getByIdAsync(
        { callerContext: ctx, queryResultSettings: { Paging: { Top: 1 } } },
        input.productId,
        input.channelId
    );
    const baseProduct = Array.isArray(productResponse) ? productResponse[0] : productResponse;
    try {
        return await getVariantsByDimensionValuesAsync(
            { callerContext: ctx, queryResultSettings: { Paging: { Top: 500 } } },
            baseProduct.MasterProductId || input.productId,
            input.channelId,
            []
        );
    } catch (error) {
        ctx.telemetry.exception(error);
        throw new Error('[IBuyboxGetDimensionPricingActionData]Unable to retieve data');
    }
}

export default createObservableDataAction({
    id: 'AllMFRMProductVariantIDs',
    action: <IAction<SimpleProduct[]>>getAllVariantsForProduct,
    input: createInput
});
